/**
 * Returns env var with checking for empty value.
 * Correctly handles server-side only vars (without NEXT_PUBLIC_ prefix).
 *
 * Takes a `{ NAME: process.env.NAME }` entry. The name is unavoidably written twice: the literal
 * `process.env.NAME` is required for Next.js/Turbopack to inline NEXT_PUBLIC_ vars into the client
 * bundle (dynamic `process.env[name]` is NOT inlined), and inlining erases the name at compile time,
 * so the object key carries it separately for the error message.
 *
 * Usage:
 * getEnvVar({ VAR_NAME: process.env.VAR_NAME });
 *
 * Note:
 * special flag ALLOW_EMPTY_ENV_VARS allows to skip env checking (useful for e2e tests).
 */
export function getEnvVar(entry: Record<string, string | undefined>): string {
  const [name, value] = extractKeyValue(entry);
  if (!value && !process.env.ALLOW_EMPTY_ENV_VARS) {
    const isServerSide = typeof window === 'undefined';
    const isPublicVar = name.startsWith('NEXT_PUBLIC_');
    // on server side all vars must be defined
    // on client side all public vars must be defined, server-only vars become ''
    if (isServerSide || isPublicVar) {
      throw new Error(`Missing env var "${name}"`);
    }
  }

  return value || '';
}

/**
 * Extracts the single [name, value] entry, ensuring exactly one was passed.
 */
function extractKeyValue(entry: Record<string, string | undefined>): [string, string | undefined] {
  const entries = Object.entries(entry);
  if (entries.length !== 1) {
    const keys = Object.keys(entry).join(', ');
    throw new Error(
      `getEnvVar expects exactly one env var entry, got ${entries.length}${keys ? `: ${keys}` : ''}`,
    );
  }

  return entries[0];
}
